import React, { createContext, useState, useEffect, JSX } from "react";
import { authService } from "@cabukaka/services";
import { User, Profile } from "@cabukaka/types";
import { useToken } from "@cabukaka/hooks";

interface AuthContextType {
  user: User | null;
  // 1. On ajoute setUser au type du contexte
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  loading: boolean;
  error: any | null;
  refreshUser: () => Promise<void>;
  logout: () => void;
  updateUser: (user: User, profileData?: Partial<Profile>) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({
  children,
}: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any | null>(null);
  const { token, removeToken } = useToken();

  const fetchUser = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const currentUser = await authService.getUserProfile(token);
      setUser(currentUser);
    } catch (err) {
      setError(err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [token]);

  const logout = () => {
    setUser(null);
    removeToken();
  };

  const updateUser = async (user: User, profileData?: Partial<Profile>) => {
    if (!user.id) throw new Error("User non identifié");
    
    try {
      await authService.updateUser(user.id, user.username, user.avatar, undefined, profileData);
      await fetchUser(); 
    } catch (error) {
      throw error;
    }
  };

  return (
    // 2. On passe setUser dans la value du Provider
    <AuthContext.Provider value={{ user, setUser, loading, error, refreshUser: fetchUser, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};